import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Anchor,
  CheckCircle2,
  History,
  MessageSquareWarning,
  ShieldQuestion,
  Undo2,
  UserRound,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";

import { cn } from "@/lib/utils";
import { logger } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/hooks/useAuth";
import { useDefectSelfcorrect, type DefectSelfcorrectStatus } from "@/hooks/useDefectSelfcorrect";
import { useDefectStatusMutations } from "@/hooks/useDefectStatusMutations";
import { SuggestCancelDialog } from "@/components/defects/SuggestCancelDialog";

/**
 * TES-2229 S8 (LH §L5.10, PT-TSK-Hub-Muster „Ersteller & Storno"-Card) — macht die
 * 4-stufige Selbstkorrektur-Entscheidungskette SICHTBAR (Provenienz → Anker-Spiegel →
 * Kontrakt-Fenster → Bedingung) statt sie als stillen Permission-Check zu verstecken:
 * der User sieht WARUM er (nicht) korrigieren darf. Verdict-SoT ist der Server
 * (rpc_defect_selfcorrect_status = dieselben Prädikate wie Guard/RPCs).
 *
 * Affordances (Kommentar 2 / TES-2217-Matrix):
 *  - „Storno anregen (auditiert)" für den Fachbereich (may_suggest_cancel) —
 *    data-testid `suggest-cancel-action` (PoolRow-Konvention, TES-2217).
 *  - „Storno aufheben" (reversibel) NUR Ersteller/Verifizierer (may_reopen).
 */
interface DefectSelfCorrectCardProps {
  defect: { id: string; title: string; status: string };
  /** Nach einer Zustandsänderung (Reopen) — Aufrufer invalidiert/refresht. */
  onChanged?: () => void;
}

function ChainStep({
  ok,
  label,
  detail,
  testId,
}: {
  ok: boolean;
  label: string;
  detail?: string;
  testId: string;
}) {
  return (
    <li className="flex items-start gap-2" data-testid={testId}>
      {ok ? (
        <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-success" aria-hidden />
      ) : (
        <XCircle className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" aria-hidden />
      )}
      <div className="min-w-0">
        <p className="text-sm leading-tight">{label}</p>
        {detail && <p className="text-xs text-muted-foreground leading-tight">{detail}</p>}
      </div>
    </li>
  );
}

/** Verdict-Text (darf/darf-nicht + GRUND) aus dem Server-Verdict ableiten. */
function verdictKey(s: DefectSelfcorrectStatus): { key: string; allowed: boolean } {
  if (s.may_cancel && s.may_cancel_path === "creator_window") {
    return { key: "defects.selfcorrect.verdict.creatorWindow", allowed: true };
  }
  if (s.may_cancel && s.may_cancel_path === "qm_role") {
    return { key: "defects.selfcorrect.verdict.qmRole", allowed: true };
  }
  if (s.may_cancel && s.may_cancel_path === "verifier_or_deputy") {
    return { key: "defects.selfcorrect.verdict.verifierDeputy", allowed: true };
  }
  if (s.may_reopen) {
    return { key: "defects.selfcorrect.verdict.mayReopen", allowed: true };
  }
  if (!s.is_creator) {
    return { key: "defects.selfcorrect.verdict.notCreator", allowed: false };
  }
  if (s.anchor_mirror.task_links || s.anchor_mirror.sap) {
    return { key: "defects.selfcorrect.verdict.anchored", allowed: false };
  }
  if (!s.window.status_open) {
    return { key: "defects.selfcorrect.verdict.statusNotOpen", allowed: false };
  }
  return { key: "defects.selfcorrect.verdict.windowClosed", allowed: false };
}

export function DefectSelfCorrectCard({ defect, onChanged }: DefectSelfCorrectCardProps) {
  const { t } = useTranslation();
  const { user, profile } = useAuth();
  const mutations = useDefectStatusMutations(profile?.active_tenant_id ?? null, user?.id);
  const { data: verdict, isLoading } = useDefectSelfcorrect(defect.id);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [reopening, setReopening] = useState(false);

  if (isLoading || !verdict) return null;

  const { key, allowed } = verdictKey(verdict);
  const anchored = verdict.anchor_mirror.task_links || verdict.anchor_mirror.sap;

  const handleReopen = async () => {
    setReopening(true);
    try {
      await mutations.reopen(defect.id);
      toast.success(t("defects.selfcorrect.reopenSuccess"));
      mutations.invalidateDefects();
      onChanged?.();
    } catch (error) {
      logger.error("Error reopening defect (Storno aufheben)", error, { defectId: defect.id });
      toast.error(t("common.error"), { description: t("defects.selfcorrect.reopenError") });
    } finally {
      setReopening(false);
    }
  };

  return (
    <Card data-testid="defect-selfcorrect-card">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm font-semibold">
          <ShieldQuestion className="h-4 w-4" aria-hidden />
          {t("defects.selfcorrect.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* 4-stufige Entscheidungskette (Hub-Muster: Provenienz → Anker → Fenster → Bedingung) */}
        <ol className="space-y-2">
          <ChainStep
            ok={verdict.is_creator}
            label={t("defects.selfcorrect.step.provenance")}
            detail={t(
              verdict.is_creator
                ? "defects.selfcorrect.step.provenanceYes"
                : "defects.selfcorrect.step.provenanceNo",
            )}
            testId="defect-selfcorrect-step-provenance"
          />
          <ChainStep
            ok={!anchored}
            label={t("defects.selfcorrect.step.anchor")}
            detail={
              anchored
                ? t("defects.selfcorrect.step.anchorLinked", {
                    kinds: [
                      verdict.anchor_mirror.task_links ? "PT-TSK" : null,
                      verdict.anchor_mirror.sap ? "SAP" : null,
                    ]
                      .filter(Boolean)
                      .join(" + "),
                  })
                : t("defects.selfcorrect.step.anchorFree")
            }
            testId="defect-selfcorrect-step-anchor"
          />
          <ChainStep
            ok={verdict.window.open}
            label={t("defects.selfcorrect.step.window")}
            detail={
              verdict.window.open
                ? t("defects.selfcorrect.step.windowOpen")
                : verdict.window.foreign_activity_at || verdict.window.assigned_to_third_party
                  ? t("defects.selfcorrect.step.windowForeign")
                  : t("defects.selfcorrect.step.windowStatus", { status: verdict.window.status })
            }
            testId="defect-selfcorrect-step-window"
          />
          <ChainStep
            ok={verdict.may_cancel || verdict.may_soft_delete || verdict.may_reopen}
            label={t("defects.selfcorrect.step.condition")}
            testId="defect-selfcorrect-step-condition"
          />
        </ol>

        {/* Verdict: darf / darf-nicht + Grund (S8-Kern — kein nackter disabled-Button) */}
        <p
          className={cn(
            "rounded-md border px-3 py-2 text-sm",
            allowed
              ? "border-success/50 bg-success/10"
              : "border-muted-foreground/30 bg-muted text-muted-foreground",
          )}
          data-testid="defect-selfcorrect-verdict"
          data-verdict={allowed ? "allowed" : "denied"}
        >
          {t(key)}
        </p>

        {/* Affordances */}
        <div className="flex flex-wrap gap-2">
          {verdict.may_suggest_cancel && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setSuggestOpen(true)}
              data-testid="suggest-cancel-action"
            >
              <MessageSquareWarning className="h-4 w-4 mr-1" aria-hidden />
              {t("defects.suggestCancel.action")}
            </Button>
          )}
          {verdict.may_reopen && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleReopen}
              disabled={reopening}
              data-testid="defect-reopen-action"
            >
              <Undo2 className="h-4 w-4 mr-1" aria-hidden />
              {defect.status === "cancelled"
                ? t("defects.selfcorrect.liftCancellation")
                : t("defects.actions.reopen")}
            </Button>
          )}
        </div>

        {/* Kontext-Fußzeile: Provenienz ≠ Privileg (LH §L5.10) */}
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <UserRound className="h-3.5 w-3.5" aria-hidden />
          <Anchor className="h-3.5 w-3.5" aria-hidden />
          <History className="h-3.5 w-3.5" aria-hidden />
          {t("defects.selfcorrect.footnote")}
        </p>
      </CardContent>

      <SuggestCancelDialog
        open={suggestOpen}
        onOpenChange={setSuggestOpen}
        defect={{ id: defect.id, title: defect.title }}
        onSuggested={onChanged}
      />
    </Card>
  );
}
